import { z } from 'zod/v4'
import type { ComputeLayer as SkedyulComputeLayer } from 'skedyul'

import {
  BuildLogSchema,
  type BuildLogEntry,
} from '../../workflows/resources/types'

// Internal use only - not re-exported to avoid duplicate exports with workflows
type InternalBuildLogEntry = BuildLogEntry

// Local type aliases for compute layer narrowing (matches skedyul package)
type ComputeLayerType = SkedyulComputeLayer
type Serverless = 'serverless'
type Dedicated = 'dedicated'

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Status
// ─────────────────────────────────────────────────────────────────────────────

export const DeploymentStatusSchema = z.enum([
  'PENDING',
  'QUEUED',
  'BUILDING',
  'SUCCEEDED',
  'FAILED',
])

export type DeploymentStatus = z.infer<typeof DeploymentStatusSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Mode
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CHECK: build/validate only (PR / non-default branch). No Executable, no provision.
 * RELEASE: create Executable; auto-provision default AppVersion (Dev) when from default branch.
 */
export const DeploymentModeSchema = z.enum(['CHECK', 'RELEASE'])

export type DeploymentMode = z.infer<typeof DeploymentModeSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Compute Layer
// ─────────────────────────────────────────────────────────────────────────────

// Re-export from skedyul for backwards compatibility
export const ComputeLayerSchema = z.enum(['serverless', 'dedicated'])
export type ComputeLayer = ComputeLayerType

// ─────────────────────────────────────────────────────────────────────────────
// Executable Status
// ─────────────────────────────────────────────────────────────────────────────

export const ExecutableStatusSchema = z.enum([
  'PENDING',
  'BUILDING',
  'STARTING',
  'RUNNING',
  'COLD',
  'STOPPING',
  'STOPPED',
  'FAILED',
  'ARCHIVED',
])

export type ExecutableStatus = z.infer<typeof ExecutableStatusSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Source Types (discriminated union)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GitHub source - clone from a GitHub repository commit
 */
export const DeploymentSourceGithubSchema = z.object({
  type: z.literal('github'),
  owner: z.string().min(1),
  repo: z.string().min(1),
  commitSha: z.string().min(1),
  branch: z.string().optional(),
  repositoryUrl: z.string().nullable().optional(),
  projectDirectory: z.string().optional(),
  githubRepoId: z.string().optional(),
  authorName: z.string().nullable().optional(),
  authorEmail: z.string().nullable().optional(),
  commitMessage: z.string().nullable().optional(),
  githubAccessToken: z.string().optional(),
})

export type DeploymentSourceGithub = z.infer<
  typeof DeploymentSourceGithubSchema
>

/**
 * S3 source - use a pre-built tar.gz from S3 (e.g., from workflow inline code)
 */
export const DeploymentSourceS3Schema = z.object({
  type: z.literal('s3'),
  buildContextS3Path: z.string().min(1),
})

export type DeploymentSourceS3 = z.infer<typeof DeploymentSourceS3Schema>

/**
 * Discriminated union of deployment sources
 */
export const DeploymentSourceSchema = z.discriminatedUnion('type', [
  DeploymentSourceGithubSchema,
  DeploymentSourceS3Schema,
])

export type DeploymentSource = z.infer<typeof DeploymentSourceSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Create Deployment Input
// ─────────────────────────────────────────────────────────────────────────────

export const CreateDeploymentInputSchema = z.object({
  /** Pre-generated deployment ID (use generateDBId('deployment')) */
  deploymentId: z.string().min(1),
  workplaceId: z.string().min(1),
  source: DeploymentSourceSchema,
  computeLayer: ComputeLayerSchema.optional(),
  imageTag: z.string().optional(),
  /** Optional app ID - if provided, an executable will be created and auto-assigned to default AppVersion */
  appId: z.string().optional(),
  /** Optional workflow version ID - if provided (and no appId), an executable will be created and linked to this WorkflowVersion */
  workflowVersionId: z.string().optional(),
  /**
   * CHECK = build/validate only (no Executable / provision).
   * RELEASE = create Executable and (for app deploys) auto-provision Dev.
   * Omit or unset → treated as RELEASE (UI redeploy / workflow deploys).
   */
  mode: DeploymentModeSchema.optional(),
  /**
   * When true, provision the default AppVersion after a successful RELEASE.
   * Omit → true for RELEASE app deploys; CHECK never provisions.
   */
  shouldProvisionDefault: z.boolean().optional(),
  /** Optional app handle for GitHub Check Run naming */
  appHandle: z.string().optional(),
})

export type CreateDeploymentInput = z.infer<typeof CreateDeploymentInputSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Create Deployment Result
// ─────────────────────────────────────────────────────────────────────────────

export const CreateDeploymentResultSchema = z.object({
  deploymentId: z.string(),
  imageTagId: z.string().nullable(),
  imageUri: z.string().nullable(),
  logs: BuildLogSchema,
  /** Executable ID created for this deployment (if appId or workflowVersionId was provided) */
  executableId: z.string().nullable().optional(),
})

export type CreateDeploymentResult = z.infer<
  typeof CreateDeploymentResultSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Schema (database record shape)
// ─────────────────────────────────────────────────────────────────────────────

export const DeploymentSchema = z.object({
  id: z.string(),
  status: DeploymentStatusSchema,

  workplaceId: z.string(),

  imageTagId: z.string().nullable().optional(),
  imageUri: z.string().nullable().optional(),

  // Compute configuration (derived from Docker image, not passed in)
  runtime: z.string().nullable().optional(),
  computeLayer: ComputeLayerSchema.nullable().optional(),

  logs: BuildLogSchema.nullable().optional(),

  startedAt: z.date().nullable().optional(),
  completedAt: z.date().nullable().optional(),
  temporalWorkflowId: z.string().nullable().optional(),
  buildContextS3Path: z.string().nullable().optional(),

  createdAt: z.date(),
  updatedAt: z.date(),
})

export type Deployment = z.infer<typeof DeploymentSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Live AppVersion Schema (for deployments with running executables)
// ─────────────────────────────────────────────────────────────────────────────

export const LiveAppVersionSchema = z.object({
  id: z.string(),
  name: z.string(),
  handle: z.string(),
  isDefault: z.boolean(),
})

export type LiveAppVersion = z.infer<typeof LiveAppVersionSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Migration Impact (for approval UI)
// ─────────────────────────────────────────────────────────────────────────────

export const MigrationImpactSchema = z.object({
  operationType: z.string(),
  resourceType: z.string(),
  resourceHandle: z.string(),
  affectedRecords: z.number().optional(),
  message: z.string().optional(),
  isDestructive: z.boolean().optional(),
})

export type MigrationImpact = z.infer<typeof MigrationImpactSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Pending Migration (for approval flow)
// ─────────────────────────────────────────────────────────────────────────────

export const PendingMigrationSchema = z.object({
  migrationId: z.string(),
  impacts: z.array(MigrationImpactSchema),
  timeoutMinutes: z.number(),
  createdAt: z.string(),
})

export type PendingMigration = z.infer<typeof PendingMigrationSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment list migration approval (summary for list views)
// ─────────────────────────────────────────────────────────────────────────────

export const DeploymentMigrationApprovalSchema = z.object({
  status: z.literal('AWAITING_APPROVAL'),
  migrationId: z.string(),
})

export type DeploymentMigrationApproval = z.infer<
  typeof DeploymentMigrationApprovalSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Queued Deployment (for showing other deployments waiting in queue)
// ─────────────────────────────────────────────────────────────────────────────

export const QueuedDeploymentSchema = z.object({
  id: z.string(),
  status: DeploymentStatusSchema,
  commitSha: z.string().nullable().optional(),
  branch: z.string().nullable().optional(),
  commitMessage: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  configVersion: z.string().nullable().optional(),
})

export type QueuedDeployment = z.infer<typeof QueuedDeploymentSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Detail Schema (for API responses)
// ─────────────────────────────────────────────────────────────────────────────

export const DeploymentDetailSchema = z.object({
  id: z.string(),
  status: DeploymentStatusSchema,
  appId: z.string().nullable().optional(),
  appName: z.string().nullable().optional(),
  appHandle: z.string().nullable().optional(),
  appVersionId: z.string().nullable().optional(),
  appVersionLabel: z.string().nullable().optional(),
  commitSha: z.string().nullable().optional(),
  branch: z.string().nullable().optional(),
  commitMessage: z.string().nullable().optional(),
  imageTagId: z.string().nullable().optional(),
  imageUri: z.string().nullable().optional(),
  runtime: z.string().nullable().optional(),
  computeLayer: ComputeLayerSchema.nullable().optional(),
  startedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  logs: BuildLogSchema.nullable().optional(),
  /** Set when CloudWatch log fetch failed (distinct from empty logs) */
  logsError: z.string().nullable().optional(),
  /** AppVersion currently using this deployment's executable (if RUNNING) */
  liveAppVersion: LiveAppVersionSchema.nullable().optional(),
  /** Executable status for this deployment */
  executableStatus: ExecutableStatusSchema.nullable().optional(),
  /** Version from skedyul.config.ts (e.g. "2.0.101") */
  configVersion: z.string().nullable().optional(),
  /** Pending migration awaiting approval (if any) */
  pendingMigration: PendingMigrationSchema.nullable().optional(),
  /** Other deployments for the same app that are pending or queued */
  queuedDeployments: z.array(QueuedDeploymentSchema).nullable().optional(),
})

export type DeploymentDetail = z.infer<typeof DeploymentDetailSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Summary Schema (for list views)
// ─────────────────────────────────────────────────────────────────────────────

export const DeploymentSummarySchema = z.object({
  id: z.string(),
  appId: z.string(),
  appName: z.string(),
  appHandle: z.string(),
  environments: z.array(z.string()),
  commitSha: z.string(),
  branch: z.string().nullable(),
  status: DeploymentStatusSchema,
  builtAt: z.string().nullable(),
  commitMessage: z.string().nullable(),
  createdAt: z.string(),
  /** AppVersions currently using this deployment's executable (if RUNNING) */
  liveAppVersions: z.array(LiveAppVersionSchema),
  /** Executable status for this deployment */
  executableStatus: ExecutableStatusSchema.nullable(),
  /** Workflow version label (e.g. "v1") when this deployment is for a workflow */
  workflowVersionLabel: z.string().nullable().optional(),
  /** Version from skedyul.config.ts (e.g. "2.0.101") */
  configVersion: z.string().nullable().optional(),
  /** Set when provision is blocked on CRM migration approval */
  migrationApproval: DeploymentMigrationApprovalSchema.nullable()
    .optional(),
})

export type DeploymentSummary = z.infer<typeof DeploymentSummarySchema>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Label Formatting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a human-readable deployment label from the deployment ID and config version.
 * Format: ${version}-${last 6 chars of deployment ID} or just ${last 6 chars} if no version.
 *
 * @example
 * formatDeploymentLabel('dpl_clxyz123abc456', '2.0.101') // '2.0.101-bc456'
 * formatDeploymentLabel('dpl_clxyz123abc456', null)      // 'bc456'
 */
export function formatDeploymentLabel(
  deploymentId: string,
  configVersion?: string | null,
): string {
  const shortId = deploymentId.slice(-6)
  return configVersion ? `${configVersion}-${shortId}` : shortId
}

// ─────────────────────────────────────────────────────────────────────────────
// Get Deployments Response
// ─────────────────────────────────────────────────────────────────────────────

export const GetDeploymentsResponseSchema = z.object({
  deployments: z.array(DeploymentSummarySchema),
})

export type GetDeploymentsResponse = z.infer<
  typeof GetDeploymentsResponseSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Deployment Build Update (for realtime events)
// ─────────────────────────────────────────────────────────────────────────────

export const DeploymentBuildUpdateSchema = z.object({
  deploymentId: z.string(),
  logs: BuildLogSchema.optional(),
  status: DeploymentStatusSchema.optional(),
})

export type DeploymentBuildUpdate = z.infer<typeof DeploymentBuildUpdateSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Executable Runtime Resolution
// ─────────────────────────────────────────────────────────────────────────────

export const ExecutableRuntimeSchema = z.object({
  executableId: z.string(),
  deploymentId: z.string(),
  appId: z.string().nullable().optional(),
  workflowVersionId: z.string().nullable().optional(),
  imageTagId: z.string().nullable().optional(),
  runtime: z.string().nullable().optional(),
  computeLayer: ComputeLayerSchema.nullable().optional(),
  invokeEndpoint: z.string().nullable().optional(),
  // New runtime metadata fields
  arn: z.string().nullable().optional(),
  status: ExecutableStatusSchema.nullable().optional(),
  lastInvokedAt: z.date().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  archivedAt: z.date().nullable().optional(),
})

export type ExecutableRuntime = z.infer<typeof ExecutableRuntimeSchema>

export const ResolveExecutableRuntimeInputSchema = z.discriminatedUnion(
  'targetType',
  [
    z.object({
      targetType: z.literal('app'),
      appId: z.string(),
      appVersionHandle: z.string(),
    }),
    z.object({
      targetType: z.literal('workflow'),
      workflowVersionId: z.string(),
    }),
  ],
)

export type ResolveExecutableRuntimeInput = z.infer<
  typeof ResolveExecutableRuntimeInputSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Bundled Workflow File (from Executable.config.workflowFiles)
// These are validated workflow files stored during deployment, before provisioning.
// WorkflowVersion records are only created during provisioning.
// ─────────────────────────────────────────────────────────────────────────────

export const BundledWorkflowFileSchema = z.object({
  /** File path relative to workflows folder */
  path: z.string(),
  /** Parsed handle from YAML */
  handle: z.string(),
  /** Parsed name from YAML */
  name: z.string(),
  /** Parsed description from YAML */
  description: z.string(),
  /** SHA-256 hash of content for diffing */
  contentHash: z.string(),
})

export type BundledWorkflowFile = z.infer<typeof BundledWorkflowFileSchema>

// Legacy schema for backwards compatibility with existing UI
export const DeploymentWorkflowVersionSchema = z.object({
  id: z.string(),
  workflowVersionId: z.string().nullable(),
  versionLabel: z.string().nullable(),
  contentHash: z.string().nullable(),
  workflow: z.object({
    id: z.string(),
    name: z.string(),
    handle: z.string(),
    description: z.string().nullable(),
  }),
  createdAt: z.string().datetime(),
})

export type DeploymentWorkflowVersion = z.infer<
  typeof DeploymentWorkflowVersionSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Get Deployment Workflows Input/Response
// ─────────────────────────────────────────────────────────────────────────────

export const GetDeploymentWorkflowsInputSchema = z.object({
  deploymentId: z.string(),
  workplaceId: z.string(),
})

export type GetDeploymentWorkflowsInput = z.infer<
  typeof GetDeploymentWorkflowsInputSchema
>

export const GetDeploymentWorkflowsResponseSchema = z.object({
  deploymentId: z.string(),
  workflowVersions: z.array(DeploymentWorkflowVersionSchema),
})

export type GetDeploymentWorkflowsResponse = z.infer<
  typeof GetDeploymentWorkflowsResponseSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Provision AppVersion Types
// ─────────────────────────────────────────────────────────────────────────────

export const ProvisionAppVersionInputSchema = z.object({
  appVersionId: z.string().min(1),
  executableId: z.string().min(1),
  env: z.record(z.string(), z.string()).optional(),
})

export type ProvisionAppVersionInput = z.infer<
  typeof ProvisionAppVersionInputSchema
>

export const ProvisionAppVersionResultSchema = z.object({
  success: z.boolean(),
  appVersionId: z.string(),
  executableId: z.string(),
  invokeEndpoint: z.string().nullable(),
  previousExecutableId: z.string().nullable(),
  errorMessage: z.string().nullable(),
})

export type ProvisionAppVersionResult = z.infer<
  typeof ProvisionAppVersionResultSchema
>

// ─────────────────────────────────────────────────────────────────────────────
// Provision Status
// ─────────────────────────────────────────────────────────────────────────────

export const ProvisionStatusSchema = z.enum([
  'PENDING',
  'STARTING',
  'HEALTH_CHECK',
  'SYNCING_TOOLS',
  'SYNCING_RESOURCES',
  'AWAITING_APPROVAL',
  'PROVISION_HANDLER',
  'ASSIGNING',
  'STOPPING_LEGACY',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
])

export type ProvisionStatus = z.infer<typeof ProvisionStatusSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Provision Step (for progress tracking)
// ─────────────────────────────────────────────────────────────────────────────

export const ProvisionStepSchema = z.object({
  name: z.string(),
  status: z.enum([
    'pending',
    'in_progress',
    'awaiting_approval',
    'completed',
    'failed',
  ]),
  startedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
})

export type ProvisionStep = z.infer<typeof ProvisionStepSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Provision Schema (database record shape for API responses)
// ─────────────────────────────────────────────────────────────────────────────

export const ProvisionSchema = z.object({
  id: z.string(),
  status: ProvisionStatusSchema,
  appVersionId: z.string(),
  executableId: z.string(),
  previousExecutableId: z.string().nullable().optional(),
  temporalWorkflowId: z.string().nullable().optional(),
  currentStep: z.string().nullable().optional(),
  steps: z.array(ProvisionStepSchema).nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  pendingMigration: PendingMigrationSchema.nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable().optional(),
  // Related data for display
  appVersionName: z.string().nullable().optional(),
  appVersionHandle: z.string().nullable().optional(),
  appId: z.string().nullable().optional(),
  appName: z.string().nullable().optional(),
  executableDeploymentId: z.string().nullable().optional(),
})

export type Provision = z.infer<typeof ProvisionSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Provision Update (for realtime events via Pusher)
// ─────────────────────────────────────────────────────────────────────────────

export const ProvisionUpdateSchema = z.object({
  provisionId: z.string(),
  status: ProvisionStatusSchema.optional(),
  currentStep: z.string().nullable().optional(),
  steps: z.array(ProvisionStepSchema).optional(),
  errorMessage: z.string().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  pendingMigration: PendingMigrationSchema.nullable().optional(),
})

export type ProvisionUpdate = z.infer<typeof ProvisionUpdateSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Generic Compute Layer Types
// ─────────────────────────────────────────────────────────────────────────────
// These types provide compile-time type safety for compute-layer-specific fields.
// Usage:
//   const serverlessDeploy: ExecutableRuntime<'serverless'> = { ... } // has lambdaArn
//   const dedicatedDeploy: ExecutableRuntime<'dedicated'> = { ... }   // has ecsServiceArn

/**
 * Serverless-specific fields for executables running on Lambda
 */
export interface ServerlessExecutableFields {
  /** Lambda function ARN */
  lambdaArn?: string
  /** Lambda function URL for direct invocation */
  functionUrl?: string
}

/**
 * Dedicated-specific fields for executables running on ECS
 */
export interface DedicatedExecutableFields {
  /** ECS service ARN */
  ecsServiceArn?: string
  /** ECS cluster ARN */
  ecsClusterArn?: string
  /** ECS task definition ARN */
  taskDefinitionArn?: string
}

/**
 * Base executable runtime fields (compute-layer agnostic)
 */
export interface ExecutableRuntimeBase<
  TComputeLayer extends ComputeLayerType = ComputeLayerType,
> {
  executableId: string
  deploymentId: string
  appId?: string | null
  workflowVersionId?: string | null
  imageTagId?: string | null
  runtime?: string | null
  computeLayer: TComputeLayer | null
  invokeEndpoint?: string | null
  arn?: string | null
  status?: ExecutableStatus | null
  lastInvokedAt?: Date | null
  errorMessage?: string | null
  archivedAt?: Date | null
}

/**
 * Generic executable runtime with compute-layer-specific fields
 *
 * @example
 * // Serverless executable with Lambda-specific fields
 * const serverlessExe: ExecutableRuntimeTyped<'serverless'> = {
 *   executableId: 'exe_123',
 *   deploymentId: 'dep_456',
 *   computeLayer: 'serverless',
 *   lambdaArn: 'arn:aws:lambda:...',
 *   functionUrl: 'https://...'
 * }
 *
 * @example
 * // Dedicated executable with ECS-specific fields
 * const dedicatedExe: ExecutableRuntimeTyped<'dedicated'> = {
 *   executableId: 'exe_123',
 *   deploymentId: 'dep_456',
 *   computeLayer: 'dedicated',
 *   ecsServiceArn: 'arn:aws:ecs:...',
 *   taskDefinitionArn: 'arn:aws:ecs:...'
 * }
 */
export type ExecutableRuntimeTyped<
  TComputeLayer extends ComputeLayerType = ComputeLayerType,
> = TComputeLayer extends Serverless
  ? ExecutableRuntimeBase<Serverless> & ServerlessExecutableFields
  : TComputeLayer extends Dedicated
    ? ExecutableRuntimeBase<Dedicated> & DedicatedExecutableFields
    : ExecutableRuntimeBase<TComputeLayer>

/**
 * Re-export type aliases for generic constraints
 */
export type { Serverless, Dedicated }
